import { ActionIcon, Loader, Table, Text, VisuallyHidden } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import {
  getListTokensQueryKey,
  useListTokens,
  useRevokeToken,
} from "../../api/generated/endpoints/identity/identity";
import type { Token } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { formatDate } from "../../ui/format";
import { TableFrame } from "../../ui/TableFrame";

/** The user's tokens in this team. The API lists every team's; the page shows the URL's. */
export function TokenList({ teamId }: { teamId: string }) {
  const id = teamId.toLowerCase();
  const tokens = useListTokens({
    query: { select: (all) => all.filter((token) => token.team_id.toLowerCase() === id) },
  });
  const [revoking, setRevoking] = useState<Token | undefined>(undefined);

  if (tokens.isPending) return <Loader size="sm" aria-label="Loading tokens" />;
  if (tokens.isError) return <ErrorAlert error={tokens.error} />;
  if (tokens.data.length === 0) {
    return <Text c="var(--cai-color-text-muted)">You have no tokens in this team.</Text>;
  }
  return (
    <>
      <TableFrame>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Access</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Expires</Table.Th>
            <Table.Th>Last used</Table.Th>
            <Table.Th>
              <VisuallyHidden>Actions</VisuallyHidden>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tokens.data.map((token) => (
            <Table.Tr key={token.id}>
              <Table.Td>
                <Text size="sm" fw={600}>
                  {token.name}
                </Text>
              </Table.Td>
              <Table.Td>{token.scopes.includes("write") ? "Read and write" : "Read only"}</Table.Td>
              <Table.Td>{formatDate(token.created_at)}</Table.Td>
              <Table.Td>{formatDate(token.expires_at)}</Table.Td>
              <Table.Td>
                {token.last_used_at === null ? "Never" : formatDate(token.last_used_at)}
              </Table.Td>
              <Table.Td align="right">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={`Revoke ${token.name}`}
                  onClick={() => setRevoking(token)}
                >
                  <Trash2 size={16} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </TableFrame>
      {revoking === undefined ? null : (
        <RevokeToken token={revoking} onClose={() => setRevoking(undefined)} />
      )}
    </>
  );
}

function RevokeToken({ token, onClose }: { token: Token; onClose: () => void }) {
  const queryClient = useQueryClient();
  const revoke = useRevokeToken({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getListTokensQueryKey() });
        onClose();
      },
    },
  });
  return (
    <ConfirmModal
      opened
      title={`Revoke ${token.name}?`}
      confirmLabel="Revoke token"
      onConfirm={() => revoke.mutate({ tokenId: token.id })}
      onCancel={onClose}
      pending={revoke.isPending}
      error={revoke.isError ? <ErrorAlert error={revoke.error} /> : null}
    >
      <Text size="sm">
        Anything that uses this token stops working at once. This cannot be undone.
      </Text>
    </ConfirmModal>
  );
}
